import { shiftCalendar } from '../../components/components/superAdmin/Pathfinder/pathfinderCalendar';

export interface VisibilityMetric {
  id: string;
  label: string;
  feature: string;
  definition: string;
  count: number;
  people: number;
  organizations: number;
  coverage: 'best-effort';
  collectionStartedAt: string | null;
}

export interface VisibilityMetrics {
  schemaVersion: 1;
  generatedAt: string;
  timeZone: string;
  period: { startDate: string; endDate: string; days: number };
  summary: { successfulActions: number; engagedPeople: number; engagedOrganizations: number };
  daily: { date: string; successfulActions: number; engagedPeople: number; engagedOrganizations: number }[];
  metrics: VisibilityMetric[];
  coverage: { status: 'partial' | 'unavailable'; collectionStartedAt: string | null; lastReceivedAt: string | null; reason: string };
  health: {
    enabled: boolean;
    queueDepth: number;
    queueCapacity: number;
    droppedEvents: number;
    failedBatches: number;
    persistedEvents: number;
    storageLimitEvents: number;
    storageLimitReached: boolean;
    estimatedEventBytes: number;
    readHorizonDays: number;
  };
}

export class VisibilityMetricsError extends Error {
  constructor(public status: number) {
    super(status === 401 || status === 403
      ? 'Super Admin access is required. Exit impersonation to view product activity.'
      : status === 400 ? 'Check your profile time zone and try again.'
      : 'Product activity could not be loaded. Please try again.');
  }
}

type JsonObject = Record<string, unknown>;
const object = (value: unknown): value is JsonObject => value != null && typeof value === 'object' && !Array.isArray(value);
const count = (value: unknown): value is number => typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
const text = (value: unknown, limit: number): value is string => typeof value === 'string' && value.length > 0 && value.length <= limit;
// Dates remain literal calendar tokens. Explicit-offset instants are validated,
// never relabelled or converted; the server owns reporting-zone boundaries.
const calendar = (value: unknown): value is string => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && shiftCalendar(value, 0) === value;
function instant(value: unknown): value is string {
  if (typeof value !== 'string' || value.length > 40) return false;
  const parts = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,7})?(?:Z|[+-](\d{2}):(\d{2}))$/.exec(value);
  return !!parts && calendar(parts[1]) && +parts[2] < 24 && +parts[3] < 60 && +parts[4] < 60
    && (parts[5] === undefined || (+parts[5] < 24 && +parts[6] < 60)) && Number.isFinite(Date.parse(value));
}
const optionalInstant = (value: unknown) => value === null || instant(value);
function reportingZone(value: unknown): value is string {
  if (!text(value, 100) || (value !== 'UTC' && !value.includes('/'))) return false;
  try { new Intl.DateTimeFormat('en-US', { timeZone: value }).format(0); return true; } catch { return false; }
}
const metricIds = new Set(['workflow.submitted', 'workflow.approved', 'document.completed', 'calendar.meeting_created', 'calendar.invitation_accepted']);
function normalizeOptionalInstants(value: unknown): unknown {
  if (!object(value)) return value;
  // The API's WhenWritingNull serializer omits these nullable fields. Normalize
  // only those known omissions; required objects and counts remain mandatory.
  const coverage = object(value.coverage) ? { ...value.coverage,
    collectionStartedAt: value.coverage.collectionStartedAt ?? null, lastReceivedAt: value.coverage.lastReceivedAt ?? null } : value.coverage;
  const metrics = Array.isArray(value.metrics) && value.metrics.length === metricIds.size
    ? value.metrics.map((metric: unknown) => object(metric) ? { ...metric, collectionStartedAt: metric.collectionStartedAt ?? null } : metric) : value.metrics;
  return { ...value, coverage, metrics };
}
function validReport(value: unknown, days: 7 | 30): value is VisibilityMetrics {
  if (!object(value) || value.schemaVersion !== 1 || !instant(value.generatedAt) || !reportingZone(value.timeZone)) return false;
  const { period, summary, coverage, health, daily, metrics } = value;
  if (!object(period) || period.days !== days || !calendar(period.startDate) || !calendar(period.endDate)
    || shiftCalendar(period.startDate, days) !== period.endDate) return false;
  if (!object(summary) || ![summary.successfulActions, summary.engagedPeople, summary.engagedOrganizations].every(count)) return false;
  if (!object(coverage) || (coverage.status !== 'partial' && coverage.status !== 'unavailable') || !optionalInstant(coverage.collectionStartedAt)
    || !optionalInstant(coverage.lastReceivedAt) || !text(coverage.reason, 4000)) return false;
  if (!object(health) || typeof health.enabled !== 'boolean' || typeof health.storageLimitReached !== 'boolean' || health.readHorizonDays !== 30
    || ![health.queueDepth, health.queueCapacity, health.droppedEvents, health.failedBatches, health.persistedEvents, health.storageLimitEvents, health.estimatedEventBytes].every(count)
    || health.queueCapacity === 0 || health.storageLimitEvents === 0 || health.estimatedEventBytes === 0) return false;
  const startDate = period.startDate;
  if (!Array.isArray(daily) || daily.length !== days || !daily.every((day: unknown, index) => object(day)
    && day.date === shiftCalendar(startDate, index) && [day.successfulActions, day.engagedPeople, day.engagedOrganizations].every(count))) return false;
  if (!Array.isArray(metrics) || metrics.length !== metricIds.size) return false;
  const seen = new Set<string>();
  return metrics.every((metric: unknown) => {
    if (!object(metric) || !text(metric.id, 80) || !metricIds.has(metric.id) || seen.has(metric.id)
      || !text(metric.label, 240) || !text(metric.feature, 80) || !text(metric.definition, 2400) || metric.coverage !== 'best-effort'
      || !optionalInstant(metric.collectionStartedAt) || ![metric.count, metric.people, metric.organizations].every(count)) return false;
    seen.add(metric.id); return true;
  });
}

export async function getVisibilityMetrics(token: string, days: 7 | 30, signal?: AbortSignal): Promise<VisibilityMetrics> {
  const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/super-admin/visibility-metrics?days=${days}`, {
    headers: { Authorization: `Bearer ${token}` }, signal, cache: 'no-store',
  });
  if (!response.ok) throw new VisibilityMetricsError(response.status);
  let payload: unknown;
  try { payload = await response.json(); } catch { throw new VisibilityMetricsError(502); }
  payload = normalizeOptionalInstants(payload);
  if (!validReport(payload, days)) throw new VisibilityMetricsError(502);
  return payload;
}
